import { HelpCircle } from 'lucide-react';
import { cn } from '@/utils';
import { QuizPlayerQuestionProps as QuizQuestionProps } from './types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';
import { useTranslation } from '@/hooks/use-translation';

type QuizPlayerQuestionProps = React.ComponentProps<'div'> & QuizQuestionProps;

export function QuizPlayerQuestion({
  question,
  choices,
  selectedAnswer,
  isAnswerValidated,
  correctAnswer,
  hint,
  isCorrect,
  title,
  onSelectAnswer = () => {},
  className,
  ...others
}: QuizPlayerQuestionProps) {
  return (
    <div
      {...others}
      className={cn(
        'mm:flex mm:flex-col mm:gap-6 mm:text-card-foreground',
        className
      )}
    >
      <div className="mm:font-semibold mm:leading-none mm:tracking-tight mm:text-xl">
        {/* <div className="mm:text-sm mm:mb-2 mm:text-muted-foreground">
          {title}
        </div> */}
        {question}
      </div>
      <div className="mm:*:mb-3 mm:*:last:mb-0">
        {choices.map((choice) => (
          <QuizPlayerChoice
            key={choice.label}
            selected={selectedAnswer === choice.value}
            disabled={isAnswerValidated}
            isCorrect={isAnswerValidated && choice.value === correctAnswer}
            isWrong={
              isAnswerValidated && choice.value === selectedAnswer && !isCorrect
            }
            onClick={() => onSelectAnswer(choice.value)}
          >
            {choice.label}
          </QuizPlayerChoice>
        ))}
      </div>

      {/* <div className="mm:flex mm:items-center">
        {hint && (
          <QuizPlayerHint
            className={
              isAnswerValidated
                ? 'mm:opacity-0 mm:pointer-events-none'
                : undefined
            }
          >
            {hint}
          </QuizPlayerHint>
        )} */}
      {/* {isAnswerValidated && <QuizPlayerFeedback isCorrect={isCorrect} />} */}
      {/* </div> */}
    </div>
  );
}

type QuizPlayerChoiceProps = React.ComponentProps<'button'> & {
  selected?: boolean;
  disabled?: boolean;
  isCorrect?: boolean;
  isWrong?: boolean;
  onClick?: () => void;
};

export function QuizPlayerChoice({
  selected,
  disabled,
  isCorrect,
  isWrong,
  onClick = () => {},
  children,
  className,
  ...others
}: QuizPlayerChoiceProps) {
  const baseClasses =
    'mm:w-full mm:p-4 mm:rounded-lg mm:bg-transparent mm:border-border mm:border-2 mm:text-foreground mm:text-left mm:transition-all mm:duration-200';

  const getStateClasses = () => {
    if (isCorrect) return 'mm:border-success mm:bg-success/10 mm:text-success ';
    if (isWrong)
      return 'mm:border-destructive mm:bg-destructive/10 mm:text-destructive ';
    if (selected) return 'mm:border-primary mm:bg-primary/10 mm:text-primary';
    if (disabled) return;
    return 'mm:hover:bg-muted mm:cursor-pointer';
  };

  return (
    <button
      {...others}
      className={cn(baseClasses, getStateClasses(), className)}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
QuizPlayerChoice.displayName = 'QuizPlayerChoice';

export type QuizPlayerHintProps = React.ComponentProps<'button'> & {};

export function QuizPlayerHint({
  children,
  className,
  ...others
}: QuizPlayerHintProps) {
  const { t } = useTranslation();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          {...others}
          asChild
          className={cn(
            'mm:text-muted-foreground/50 mm:text-sm mm:transition-all',
            className
          )}
        >
          <div>
            <HelpCircle size={16} className="mm:inline mm:mr-2" />
            {t('quizHint', 'Suggerimento')}
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="mm:bg-popover mm:text-popover-foreground"
          side="top"
          align="start"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
QuizPlayerHint.displayName = 'QuizPlayerHint';

export function QuizPlayerFeedback({
  isCorrect,
}: React.ComponentProps<'div'> & { isCorrect?: boolean }) {
  return (
    <div
      className={`mm:p-4 mm:rounded-lg ${
        isCorrect
          ? 'mm:bg-green-50 mm:text-green-700'
          : 'mm:bg-red-50 mm:text-red-700'
      }`}
    >
      {isCorrect ? 'Correct!' : 'Incorrect. Try again!'}
    </div>
  );
}
QuizPlayerFeedback.displayName = 'QuizPlayerFeedback';
